/**
 * Three-way merge — resolves conflicts by comparing base, local, and server states.
 *
 * Diffs base→local and base→server field-by-field.
 * If changes are disjoint, auto-merges. If the same field
 * changed on both sides, reports a conflict for that field.
 */

export interface MergeResult {
  merged: Record<string, unknown>
  conflicts: FieldConflict[]
  autoResolved: boolean
}

export interface FieldConflict {
  field: string
  baseValue: unknown
  localValue: unknown
  serverValue: unknown
}

export function threeWayMerge(
  base: Record<string, unknown>,
  local: Record<string, unknown>,
  server: Record<string, unknown>
): MergeResult {
  const allKeys = new Set([...Object.keys(base), ...Object.keys(local), ...Object.keys(server)])

  const merged: Record<string, unknown> = {}
  const conflicts: FieldConflict[] = []

  // Fields managed by the server — never conflict
  const serverManaged = new Set(['version', 'lastModified', 'lastModifiedBy'])

  for (const key of allKeys) {
    if (serverManaged.has(key)) {
      merged[key] = server[key]
      continue
    }

    const baseVal = base[key]
    const localVal = local[key]
    const serverVal = server[key]

    const localChanged = !deepEqual(baseVal, localVal)
    const serverChanged = !deepEqual(baseVal, serverVal)

    if (!localChanged && !serverChanged) {
      merged[key] = baseVal
    } else if (localChanged && !serverChanged) {
      merged[key] = localVal
    } else if (!localChanged && serverChanged) {
      merged[key] = serverVal
    } else if (deepEqual(localVal, serverVal)) {
      merged[key] = localVal
    } else {
      conflicts.push({
        field: key,
        baseValue: baseVal,
        localValue: localVal,
        serverValue: serverVal,
      })
      merged[key] = serverVal
    }
  }

  return {
    merged,
    conflicts,
    autoResolved: conflicts.length === 0,
  }
}

export function changedFields(
  base: Record<string, unknown>,
  current: Record<string, unknown>
): Record<string, unknown> {
  const diff: Record<string, unknown> = {}
  const allKeys = new Set([...Object.keys(base), ...Object.keys(current)])

  for (const key of allKeys) {
    if (!deepEqual(base[key], current[key])) {
      diff[key] = current[key]
    }
  }

  return diff
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== typeof b) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((val, i) => deepEqual(val, b[i]))
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every(key => deepEqual(aObj[key], bObj[key]))
  }

  return false
}
